import * as React from "react";
import { WarningOutlined } from "@ant-design/icons";
import { AttrTypeName } from "src/types/AttrType";
import { Cell2Input, Cell2Number } from "src/components/Sheet2/components";
import { InputNumberProps } from "antd";
import { getEditorInfo } from "src/common/attrEdit";
import { Cell2Text } from "src/components/Sheet2/components/Cell2Text";
import { PropsCell } from "src/components/Sheet2/PropsCell2";
import { InputProps } from "antd/lib";
import {
  makeInputProps,
  makeTextAreaProps,
} from "src/common/attrEdit/components";
import { cvtError } from "src/common/onError";
import { translateFormula } from "src/common/formula/translateFormula";
import { getFormulaUids } from "src/common/formula/getFormulaUids";
import { Cell2DatePicker } from "src/components/Sheet2/components/Cell2DatePicker";
import { TextAreaProps } from "antd/lib/input";
import { Cell2Switch, PropsCell2Switch } from "./cellComponents/Cell2Switch";
import { ZBom3PosColumn, ZBom3PosRow } from "./ZBom3Position";
import { Bom3PositionStore } from "./Bom3PositionsStore";
import { Bom3PosColumn } from "./Bom3PosColumn";
import { Cell2FormulaView } from "./cellComponents/Cell2FormulaView";
import { Cell2ReadonlyText } from "./cellComponents/Cell2ReadonlyText";
import { CellBom3MaterialSelect } from "./cellComponents/CellBom3MaterialSelect";
import { Cell2ColorModel } from "./cellComponents/Cell2ColorModel";
import {
  makeEmptySizeVar,
  SizeVarChange,
} from "./cellComponents/SizeVarChange";

class ColCtx {
  constructor(public store: Bom3PositionStore) {}

  typeName(type: number): string {
    return this.store.typesMap[type] ?? String(type);
  }

  get disabledUpdate() {
    return !this.store.possibleUpdate;
  }
}

export const createBom3Columns = (
  store: Bom3PositionStore,
): Bom3PosColumn[] => {
  const { data } = store;
  if (data.status !== "ready") return [];
  const columns = data.result.schema.attributes;
  const ctx = new ColCtx(store);
  return columns.map(
    (col) =>
      ({
        key: col.id,
        header: col.displayName,
        isColorModel: col.behavior === "COLOR_MODEL",
        ...cellCommon(col, ctx),
      }) satisfies Bom3PosColumn,
  );
};

type FnRender = (
  col: ZBom3PosColumn,
  ctx: ColCtx,
) => Pick<Bom3PosColumn, "isCell" | "renderCell">;

// eslint-disable-next-line consistent-return
const cellCommon: FnRender = (col, ctx) => {
  // Здесь выполняется контроль компилятором наличия обработчиков всех кейсов.
  // Если включить default или общий return, то контроль пропадёт.
  // eslint-disable-next-line default-case
  switch (col.behavior) {
    case "NONE":
      return cellStd(col, ctx);
    case "COLOR_MODEL":
      return renderColorModel(col, ctx);
    case "SIZE_VARIATION":
      return renderSizeVar(col, ctx);
  }
};

const renderColorModel: FnRender = (col, ctx) => {
  const typeName = ctx.typeName(col.type);
  if (typeName === AttrTypeName.string) {
    return colorModelCell(col, ctx);
  }
  return cellDefault(col, ctx);
};

const renderSizeVar: FnRender = (col, ctx) => {
  const typeName = ctx.typeName(col.type);
  if (typeName === AttrTypeName.string) {
    return sizeVarCell(col, ctx);
  }
  return cellDefault(col, ctx);
};

const cellStd: FnRender = (col, ctx) => {
  const typeName = ctx.typeName(col.type);
  // Здесь не требуется обработка всех кейсов, т.к. довольно много типов не поддерживаются
  // eslint-disable-next-line default-case
  switch (typeName) {
    case AttrTypeName.int:
      return cellNumber(col, ctx, 0);
    case AttrTypeName.number:
      return cellNumber(col, ctx);
    case AttrTypeName.string:
      return cellText(col, ctx);
    case AttrTypeName.boolean:
      return cellBool(col, ctx);
    case AttrTypeName.formula:
      return cellFormula(col, ctx);
    case AttrTypeName.linkedValue:
      return cellLink(col, ctx);
    case AttrTypeName.date:
      return cellDate(col, ctx);
    case AttrTypeName.object:
      return cellMaterial(col, ctx);
  }
  return cellDefault(col, ctx);
};

const cellDefault: FnRender = (col, ctx) => ({
  isCell: false,
  renderCell: () => (
    <div
      title={`Unsupported type: ${ctx.typeName(col.type)} for behavior=${col.behavior}`}
    >
      <WarningOutlined />
    </div>
  ),
});

const cellLink: FnRender = (col, ctx) => ({
  isCell: false,
  renderCell: ({ attributes }) => {
    const { targetType, viewStyle } = col;
    const cell = attributes[col.id];
    const isImage =
      !!targetType && ctx.typeName(targetType) === AttrTypeName.image;
    let value = cell?.value;

    // LPLM-5409
    if (
      typeof value === "string" &&
      !!targetType &&
      (ctx.typeName(targetType) === AttrTypeName.number ||
        ctx.typeName(targetType) === AttrTypeName.formula)
    ) {
      // Здесь метаданные приходят не для ссылки, а для конечного объекта. Т.е. соответствуют targetType
      const meta = getEditorInfo(viewStyle);
      if (
        meta?.component?.editor === "InputNumber" ||
        meta?.component?.editor === "Formula"
      ) {
        const { precision } = meta.component;
        if (precision) {
          const numValue = +value;
          if (!Number.isNaN(numValue)) {
            value = numValue.toFixed(precision);
          }
        }
      }
    }

    return <Cell2ReadonlyText isImage={isImage}>{value}</Cell2ReadonlyText>;
  },
});

const cellNumber = (
  col: ZBom3PosColumn,
  ctx: ColCtx,
  srcPrecision?: number,
): ReturnType<FnRender> => {
  const editorProps: InputNumberProps = {
    precision: srcPrecision,
    disabled: ctx.disabledUpdate,
  };
  const component = getEditorInfo(col.viewStyle)?.component;
  if (component?.editor === "InputNumber") {
    if (typeof editorProps.precision !== "number") {
      editorProps.precision = component.precision ?? undefined;
    }
    editorProps.min = component.min ?? undefined;
    editorProps.max = component.max ?? undefined;
    editorProps.addonAfter = component.addonAfter;
    editorProps.addonBefore = component.addonBefore;
  }
  return {
    isCell: true,
    renderCell: (row, y, x, ctrl) => (
      <Cell2Number
        cell={mkCell({
          x,
          y,
          col,
          row,
          ctx,
          emptyValue: null,
          toEdit: (v) => (typeof v === "string" ? +v : v),
          fromEdit: (v) =>
            v === null || v === "" || v === undefined ? null : String(v),
        })}
        editorProps={{
          ...editorProps,
          onFocus: (e) => {
            ctrl?.onFocus?.(e.target);
          },
        }}
      />
    ),
  };
};

const cellText: FnRender = (col, ctx) => {
  const component = getEditorInfo(col.viewStyle)?.component;
  return {
    isCell: true,
    renderCell: (row, y, x, ctrl) => {
      const cell: PropsCell = mkCell({ x, y, col, row, ctx, emptyValue: "" });
      if (component?.editor === "TextArea") {
        const editorProps: TextAreaProps = makeTextAreaProps(component);
        editorProps.disabled = ctx.disabledUpdate;
        editorProps.onFocus = (e) => ctrl?.onFocus?.(e.target);
        return <Cell2Text cell={cell} editorProps={editorProps} />;
      }
      const inputProps: InputProps =
        component?.editor === "Input" ? makeInputProps(component) : {};
      inputProps.onFocus = (e) => {
        ctrl?.onFocus?.(e.target);
      };
      inputProps.disabled = ctx.disabledUpdate;
      return <Cell2Input cell={cell} editorProps={inputProps} />;
    },
  };
};

const cellDate: FnRender = (col, ctx) => ({
  isCell: true,
  renderCell: (row, y, x, ctrl) => {
    const cell = mkCell({ x, y, col, row, ctx, emptyValue: null });
    const editorProps = {
      format: "DD.MM.YYYY",
      disabled: ctx.disabledUpdate,
      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
        ctrl?.onFocus?.(e.target);
      },
    };
    return <Cell2DatePicker cell={cell} editorProps={editorProps} />;
  },
});

const cellBool: FnRender = (col, ctx) => ({
  isCell: true,
  renderCell: (row, y, x, ctrl) => {
    const cell = mkCell({ x, y, col, row, ctx, emptyValue: "false" });
    const editorProps: PropsCell2Switch = {
      makeBool: (value) => value === "true",
      makeValue: (checked) => (checked ? "true" : "false"),
      disabled: ctx.disabledUpdate,
      onFocus: (e) => ctrl?.onFocus?.(e.target),
    };
    return <Cell2Switch cell={cell} editorProps={editorProps} />;
  },
});

const cellFormula: FnRender = (col, ctx) => {
  const edInfo = getEditorInfo(col.viewStyle);
  const comp = edInfo?.component;
  try {
    if (comp?.editor !== "Formula")
      throw Error("В настройках атрибута должен быть выбран компонент Formula");
    // В данной реализации предполагается, что между формулами нет зависимостей
    // Иначе нужно было бы вычислять порядок выполнения, чтобы результаты одной формулы правильно передавались в другую
    const cmdList = translateFormula(comp.formula);
    const attrs = getFormulaUids(cmdList);
    return {
      isCell: false,
      renderCell: (row) => {
        const attr2cell: Record<string, string> = {};
        attrs.forEach((attrId) => {
          attr2cell[attrId] = mkCellKeyByColId(attrId, row);
        });
        return (
          <Cell2FormulaView
            sheetStore={ctx.store.sheetStore}
            row={row}
            cmdList={cmdList}
            attr2cell={attr2cell}
            precision={comp.precision}
            update={
              ctx.store.possibleUpdate
                ? (value) => ctx.store.sendValue(row.rowId, col.id, value)
                : undefined
            }
            addonBefore={comp.addonBefore}
            addonAfter={comp.addonAfter}
          />
        );
      },
    };
  } catch (e) {
    let msg = cvtError(e).message;
    if (comp?.editor === "Formula") {
      msg += ` для формулы ${comp.formula}`;
    }
    return {
      isCell: false,
      renderCell: () => (
        <div title={msg}>
          <WarningOutlined />
        </div>
      ),
    };
  }
};

const cellMaterial: FnRender = (col, ctx) => {
  const { objMaterial } = ctx.store.settings;
  return {
    isCell: true,
    renderCell: (row, y, x, ctrl) => {
      const cell = mkCell({ x, y, col, row, ctx, emptyValue: null });

      const cellValue = row.attributes[col.id];
      return (
        <CellBom3MaterialSelect
          title={col.displayName}
          cell={cell}
          row={row}
          objMaterial={objMaterial}
          cellValue={cellValue}
          positionStore={ctx.store}
          disabled={ctx.disabledUpdate}
          onFocus={(e) => ctrl?.onFocus?.(e.target)}
        />
      );
    },
  };
};

const colorModelCell: FnRender = (col, ctx) => {
  const { objColor } = ctx.store.settings;
  return {
    isCell: true,
    renderCell: (row, y, x, ctrl) => {
      const cell = mkCell({ x, y, col, row, ctx, emptyValue: null });
      return (
        <Cell2ColorModel
          cell={cell}
          objColorModel={objColor}
          positionStore={ctx.store}
          cellValue={row.attributes[col.id]}
          disabled={ctx.disabledUpdate}
          title={col.displayName}
          onFocus={(e) => ctrl?.onFocus?.(e.target)}
        />
      );
    },
  };
};

const sizeVarCell: FnRender = (col, ctx) => ({
  isCell: true,
  renderCell: (row, y, x, ctrl) => {
    const emptyValue = makeEmptySizeVar(col, row);
    const cell = mkCell({ x, y, col, row, ctx, emptyValue });
    return (
      <SizeVarChange
        cell={cell}
        col={col}
        row={row}
        store={ctx.store}
        onFocus={(e) => ctrl?.onFocus?.(e.target)}
      />
    );
  },
});

const mkCellKey = (col: ZBom3PosColumn, row: ZBom3PosRow): string =>
  mkCellKeyByColId(String(col.id), row);

const mkCellKeyByColId = (colId: string, row: ZBom3PosRow): string =>
  `${colId}:${row.rowId}`;

type ParamsMkCell = {
  x: number;
  y: number;
  ctx: ColCtx;
  col: ZBom3PosColumn;
  row: ZBom3PosRow;
  emptyValue: unknown;
  toEdit?: (value: string | null | undefined) => unknown;
  fromEdit?: (value: unknown) => string | null;
};

const mkCell = ({
  x,
  y,
  col,
  row,
  ctx,
  emptyValue,
  toEdit,
  fromEdit,
}: ParamsMkCell): PropsCell => {
  const { attributes } = row;
  const save = async (rawValue: unknown) => {
    const dstValue = fromEdit ? fromEdit(rawValue) : rawValue;
    if (dstValue !== null && typeof dstValue !== "string")
      throw Error(`Неподходящий тип данных: ${typeof dstValue}`);
    const result = await ctx.store.saveValue(row.rowId, col.id, dstValue);
    return () => {
      ctx.store.updatePositions(result);
    };
  };
  const srcValue = attributes[col.id]?.value;
  const value = toEdit ? toEdit(srcValue) : srcValue;
  return {
    cellKey: mkCellKey(col, row),
    store: ctx.store.sheetStore,
    pos: { x, y },
    value,
    emptyValue,
    save,
  };
};
